import { isDefined, isEmpty, isNotDefined } from '@sgme/fp';

import type { CheckForwardAccumulatorParams } from '@/store/forwardAccumulator/api/tradeCapture/checkForwardAccumulator.ts';
import type {
  ChangedFields,
  CheckForwardAccumulatorBody,
  TCRequestForwardAccumulator,
  TradeCaptureAccumulatorLeg,
  TradeCaptureAccumulatorScheduleItemRequest,
} from '@/store/forwardAccumulator/api/tradeCapture/request.model.ts';
import type { TcAccumulatorHedgeType } from '@/store/forwardAccumulator/api/tradeCapture/response.model.ts';
import type { AccumulatorScheduleLine, HedgeType } from '@/store/forwardAccumulator/state.ts';
import type { AppState } from '@/store/store.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { removeUndefinedValues } from '@/utils/removeUndefinedValues.ts';

export function getCheckForwardAccumulatorBody(
  params: CheckForwardAccumulatorParams,
  state: AppState,
): CheckForwardAccumulatorBody {
  const {
    hedgeType,
    premiumDate,
    clientsWay,
    notional,
    notionalCurrencyIndex,
    amountMode,
    premiumTypeCurrencyIndex,
    leverage,
    leverageAmount,
    hedgeCurrencyIndex,
    hedgeAmount,
    spotHedge,
    strike,
    firstFixingDate,
    firstFixingTenor,
    lastFixingDate,
    lastFixingTenor,
    fixingFrequency,
    numberOfFixings,
    isCrossed,
    crossCurrency,
    fixingReference1,
    fixingReference2,
    stepDown,
    stepUp,
    cashSettlementCurrency,
    akoTrigger,
    ekiTrigger,
    step,
    schedule,
  } = params.properties ?? {};

  const currencyPair = params.currencyPair;
  const tradeCapture = state.forwardAccumulator[params.tileId]?.tradeCapture;

  if (isNotDefined(tradeCapture)) {
    throw new Error('Trade capture data is required to check forward accumulator');
  }

  return [
    {
      sessionId: tradeCapture.sessionId,
      tileId: params.tileId,
      productType: 'FxForwardAccumulator',
      request: {
        idVersion: tradeCapture.version + 1,
        changedFields: {
          ...removeUndefinedValues({
            currencyPair: params.properties?.currencyPair,
            hedgeType: mapToTCHedgeType(hedgeType),
            hedgeAmountInCcy1String: hedgeCurrencyIndex === 1 ? (hedgeAmount?.toString() ?? undefined) : undefined,
            hedgeAmountInCcy2String: hedgeCurrencyIndex === 2 ? (hedgeAmount?.toString() ?? undefined) : undefined,
            hedgePriceString: spotHedge === null ? null : spotHedge?.toString(),
            markupCurrency: isDefined(params.properties?.markupCurrency)
              ? params.properties?.markupCurrency
              : undefined,
          } satisfies Partial<ChangedFields>),
          legs: {
            0: {
              ...removeUndefinedValues({
                productName: 'FxForwardAccumulator',
                premiumPaymentCurrency: mapToPriceCurrency(premiumTypeCurrencyIndex, currencyPair),
                premiumPaymentDateString: isDefined(premiumDate) ? premiumDate : undefined,
                side: clientsWay,
                settlementFrequency: params.properties?.settlementFrequency,
                amountString: notional === null ? '' : notional?.toString(),
                amountCurrency: mapToCurrency(notionalCurrencyIndex, currencyPair),
                amountSplitType: amountMode,
                leverageString: leverage?.toString(),
                leverageAmountString: leverageAmount?.toString(),
                strikeString: strike === null ? null : strike?.toString(),
                stepString: step === null ? null : step?.toString(),
                step2String: ekiTrigger === null ? null : ekiTrigger?.toString(),
                barrier: isDefined(akoTrigger) ? { levelString: akoTrigger.toString() } : undefined,
                fixingFrequency,
                numberOfFixings,
                isCrossed,
                crossCurrency,
                fixingReference1,
                fixingReference2,
                stepUp,
                stepDown,
                firstFixingDateString: firstFixingDate || firstFixingTenor,
                schedule: mapToTradeCaptureSchedule(schedule),
                cashSettlementCurrency: cashSettlementCurrency,
                expiryDateString: lastFixingDate || lastFixingTenor,
              } satisfies Partial<TradeCaptureAccumulatorLeg>),
            },
          },
        },
      },
    } satisfies TCRequestForwardAccumulator,
  ];
}

function mapToTCHedgeType(hedgeType: HedgeType | null | undefined): TcAccumulatorHedgeType | null | undefined {
  if (hedgeType === 'Live') {
    return 'None';
  }

  return hedgeType;
}

const mapToCurrency = (
  amountCurrency: CurrencyIndex | undefined | null,
  currencyPair: string | undefined | null,
): string | undefined => {
  if (isNotDefined(amountCurrency) || isNotDefined(currencyPair) || isEmpty(currencyPair)) {
    return undefined;
  }

  const currencies = currencyPair.split('/');
  return currencies[amountCurrency - 1];
};

// const mapAkoTriggerToBackend = (akoTrigger: string | null | undefined): RequestBarrier | null | undefined => {
//   if (isDefined(akoTrigger)) {
//     return akoTrigger === '' ? null : { levelString: akoTrigger };
//   }

//   return undefined;
// };

const mapToPriceCurrency = (
  premiumPaymentCurrency: CurrencyIndex | null | undefined,
  currencyPair: string | null | undefined,
): string | undefined => {
  if (isNotDefined(currencyPair) || isNotDefined(premiumPaymentCurrency)) {
    return undefined;
  }

  const [currency1, currency2] = currencyPair.split('/');
  switch (premiumPaymentCurrency) {
    case 1:
      return currency1;
    case 2:
      return currency2;
    default:
      return undefined;
  }
};

function mapToTradeCaptureSchedule(
  schedule: Record<string, Partial<AccumulatorScheduleLine> | null> | null | undefined,
): Record<string, Partial<TradeCaptureAccumulatorScheduleItemRequest> | null> | null | undefined {
  if (schedule === undefined) {
    return undefined;
  }
  if (schedule === null) {
    return null;
  }

  const result = Object.entries(schedule).reduce(
    (acc, [key, leg]) => {
      acc[key] =
        leg === null
          ? null
          : {
              fixingDateString: leg.fixingDate,
              paymentDateString: isDefined(leg.paymentDate) ? leg.paymentDate : undefined,
              strikeString: leg.strike?.toString(),
              // strikeDownString: leg.strikeDown?.toString(),
              // stepDownString: leg.ekiDown?.toString(),
              // stepUpString: leg.ekiUp?.toString(),
              // strikeUpString: leg.strikeUp?.toString(),
              stepString: leg.step?.toString(),
              amountString: leg.amount?.toString(),
              leverageAmountString: leg.leverageAmount?.toString(),
            };
      return acc;
    },
    {} as Record<string, Partial<TradeCaptureAccumulatorScheduleItemRequest | null>>,
  );

  return isEmpty(Object.keys(result)) ? undefined : result;
}
